import { useContext } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import Constants from 'expo-constants'
import { LinearGradient } from 'expo-linear-gradient'
import { useRouter, type Href } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { basic } from '@/constants/colors'
import { brandTheme } from '@/config/brandTheme'
import { UserFlags } from '@/constants/common'
import { GET_USER, GET_USER_CONNECTED_APPS, GET_USER_PAYMENT_METHODS } from '@/constants/graphql/queries'
import { cn } from '@/utils/cn'
import { copyToClipboard } from '@/utils/copy'
import { maxKYC1Flag } from '@/utils/user'
import { AuthContext } from '@/stores/auth'
import { Button, ModalBody, ModalFooter, ModalHeader, ModalTitle, useModal, useToast } from '@/components/base'
import CustomerServiceButton from '@/components/CustomerServiceButton'
import { ChevronRight, Copy, EditOutline, Person, Scan } from '@/components/icon'
import BrandLogo from '@/components/BrandLogo'
import { Stack, TabScreenContent } from '@/components/layout'
import NotificationButton from '@/components/NotificationButton'
import { CustomerServiceContext } from '@/components/providers/CustomerService'
import ScreenHeader from '@/components/ScreenHeader'
import { SelectPaymentMethodContext } from '@/components/SelectPaymentMethodScreen'
import VipCard from '@/components/VipCard'

const screenOptions = { header: () => <Header /> }

export default function MyScreen() {
  const { setSelectedBuyPayment, setSelectedBuyPaymentText, setSelectedSellPayment } =
    useContext(SelectPaymentMethodContext)
  const { t } = useTranslation()
  const auth = AuthContext.useActorRef()
  const { openModal, closeModal } = useModal()

  const handleLogout = async () => {
    closeModal()
    setSelectedBuyPayment(undefined)
    setSelectedBuyPaymentText('')
    setSelectedSellPayment({})
    // Let navigationManager handle redirecting to the landing page
    auth.send({ type: 'LOGOUT' })
  }

  const onLogout = () => {
    openModal({
      children: (
        <>
          <ModalHeader className={'p-4'}>
            <ModalTitle>{t('profile:logoutTitle')}</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Text className={'text-sm text-text-secondary'}>{t('profile:logoutDesc')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button className={'flex-1'} variant={'outline'} size={'md'} onPress={closeModal}>
              {t('cancel')}
            </Button>
            <Button className={'flex-1'} size={'md'} onPress={handleLogout}>
              {t('profile:logout')}
            </Button>
          </ModalFooter>
        </>
      ),
      onClose: closeModal
    })
  }

  return (
    <>
      <Stack.Screen options={screenOptions} />

      <TabScreenContent>
        <SafeAreaView className={'flex-1'}>
          <ScrollView className={'pb-4'}>
            <List />
            <View className={'mt-6 px-4'}>
              <Button intent={'neutral'} variant={'outline'} size={'md'} onPress={onLogout}>
                {t('profile:logout')}
              </Button>
            </View>
          </ScrollView>
        </SafeAreaView>
      </TabScreenContent>
    </>
  )
}

function Header() {
  const router = useRouter()
  const { top } = useSafeAreaInsets()

  return (
    <View className={'bg-background-brand'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={<CustomerServiceButton isHome={true} />}
        centerElement={<BrandLogo variant={'wordmark'} />}
        rightElement={
          <>
            <Pressable className={'size-10 items-center justify-center'} onPress={() => router.push('/scan2pay')}>
              {({ pressed }) => <Scan className={cn('size-6 text-white', pressed && 'opacity-50')} />}
            </Pressable>
            <NotificationButton isHome={true} />
          </>
        }
      />
    </View>
  )
}

type Group = { label: string; pathname: string; status?: string; action?: () => void }[]

function List() {
  const toast = useToast()
  const router = useRouter()
  const { t } = useTranslation()
  const { openChat } = useContext(CustomerServiceContext)

  const user = AuthContext.useSelector((state) => state.context.values?.user)
  const { data: userData } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    },
    skip: !user?.id
  })

  const { data: paymentMethodData } = useQuery(GET_USER_PAYMENT_METHODS, { skip: !user?.id })
  const { data: connectedAppsData } = useQuery(GET_USER_CONNECTED_APPS, { skip: !user?.id })

  const nickname = userData?.user?.nickname
  const phone = userData ? EncryptPhone(userData.user.phone.slice(userData.user.area.toString().length)) : '−'
  const flags = userData && userData.user && maxKYC1Flag(userData.user.flags)
  let kycDisplayStatus = t('profile:notVerified')
  switch (flags) {
    case UserFlags.KYC_1_REJECTED:
      kycDisplayStatus = t('profile:kycRejected')
      break
    case UserFlags.KYC_1_SUBMITTED:
      kycDisplayStatus = t('profile:kycReviewing')
      break
    case UserFlags.KYC_1_VERIFIED:
      kycDisplayStatus = t('profile:kycVerified')
      break
  }

  let paymentMethodStatus = ''
  if (paymentMethodData?.getUserPaymentMethods.length === 0) {
    paymentMethodStatus = t('profile:notAdded')
  }
  const connectedAppsCount = connectedAppsData?.getUserConnectedApps?.length ?? 0
  const connectedAppsStatus =
    connectedAppsCount === 0 ? t('profile:connectedAppsNotConnected') : `${connectedAppsCount}`

  let withdrawalPasswordStatus = ''
  if (userData && userData.user) {
    withdrawalPasswordStatus = userData.user.withdrawalPasswordEnabled ? t('profile:set') : t('profile:notSet')
  }

  const group1: Group = [
    {
      label: t('profile:nickname'),
      pathname: '/setting/nickname',
      status: nickname ?? t('profile:notSet')
    },
    {
      label: t('profile:phone'),
      pathname: '',
      status: phone
    },
    {
      label: t('profile:userId'),
      pathname: '',
      status: t('profile:userIdValue', { id: user?.id }),
      action: () => {
        copyToClipboard(t('profile:userIdValue', { id: user?.id }))
        toast.show({ variant: 'success', description: t('profile:copied') })
      }
    }
  ]

  const group2: Group = [
    { label: t('profile:kyc'), pathname: '/setting/kyc', status: kycDisplayStatus },
    { label: t('profile:loginPassword'), pathname: '/setting/change-password', status: '' },
    { label: t('profile:txPassword'), pathname: '/setting/tx-password', status: withdrawalPasswordStatus },
    { label: t('profile:paymentMethod'), pathname: '/setting/payment-method/', status: paymentMethodStatus },
    { label: t('profile:connectedApps'), pathname: '/setting/connected-apps', status: connectedAppsStatus }
  ]

  const group3: Group = [
    { label: t('profile:notification'), pathname: '/setting/notification', status: '' },
    { label: t('profile:contactCS'), pathname: '', status: '', action: openChat },
    {
      label: t('profile:version'),
      pathname: '',
      status: Constants.expoConfig?.version
    }
  ]

  const groups = [group1, group2, group3]

  return (
    <>
      {/* Profile */}
      <View className={'flex-row gap-3 bg-background-brand p-4'}>
        <View className={'size-12 items-center justify-center rounded-full'} style={{ backgroundColor: '#E5F3FF' }}>
          <Person className={'size-8 text-primary'} />
        </View>
        <View>
          <Pressable
            onPress={() => {
              router.push('/setting/nickname')
            }}
            className={'flex-row items-center gap-1'}
          >
            <Text className={'text-base font-medium text-white'}>{nickname ?? t('profile:nicknameNotSet')}</Text>
            <EditOutline className={'size-4 text-white'} />
          </Pressable>
          <Pressable
            onPress={() => {
              copyToClipboard(t('profile:userIdValue', { id: user?.id }))
              toast.show({ variant: 'success', description: t('profile:copied') })
            }}
            className={'flex-row items-center gap-0.5'}
          >
            <Text className={'text-sm text-white opacity-70'}>{t('profile:userIdValue', { id: user?.id })}</Text>

            <Copy className={'size-4 text-white opacity-70'} />
          </Pressable>
        </View>
      </View>
      {/* Gradient */}
      <LinearGradient colors={[brandTheme.DEFAULT, basic.gray[5]]}>
        <View className={'h-[100px]'} />
      </LinearGradient>
      {/* VIP Card */}
      <Pressable
        className={'mx-4 mt-[-100px] rounded-xl border border-border-10 bg-white'}
        onPress={() => router.push('/setting/vip')}
      >
        <View className={'py-3 pl-4 pr-3'}>
          <VipCard hasLabel={false} />
        </View>
        <View className={'flex-row items-center gap-2 p-4'}>
          <View className={'flex-row items-center justify-center rounded-full bg-primary-subtle py-1.5 pl-3 pr-2'}>
            <Text className={'text-xs font-medium text-info'}>{t('profile:upgradeGuide')}</Text>
            <ChevronRight className={'size-4 text-info'} />
          </View>
          <Text className={'text-xs font-medium text-text-tertiary'}>{t('profile:upgradeHint')}</Text>
        </View>
      </Pressable>
      {/* Settings Cards */}
      {groups.map((group, groupIndex) => (
        <View
          className={'mx-4 mt-4 overflow-hidden rounded-xl border border-border-10 bg-background-primary'}
          key={groupIndex}
        >
          {group.map((item, index) => (
            <Pressable
              key={index}
              onPress={() => {
                if (item.pathname) router.navigate(item.pathname as Href)
                else item.action?.()
              }}
            >
              {({ pressed }) => (
                <View className={cn('bg-background-primary', pressed && 'bg-background-secondary')}>
                  <View
                    className={cn(
                      'ml-4 h-12 flex-row items-center border-border-10 pr-4',
                      index !== group.length - 1 && 'border-b'
                    )}
                  >
                    <Text className={'flex-1 text-base text-text-primary'}>{item.label}</Text>
                    <View className={'flex-row items-center gap-x-2'}>
                      <Text className={'text-base text-text-tertiary'}>{item.status}</Text>
                      {item.action && item.label !== t('profile:contactCS') && (
                        <Copy className={'size-6 text-text-quaternary'} />
                      )}
                      {(!!item.pathname || item.label === t('profile:contactCS')) && (
                        <ChevronRight className={'size-6 text-text-tertiary'} />
                      )}
                    </View>
                  </View>
                </View>
              )}
            </Pressable>
          ))}
        </View>
      ))}
    </>
  )
}

function EncryptPhone(phone: string) {
  if (phone.length <= 6) {
    return phone
  }
  return `${phone.slice(0, 3)}${'*'.repeat(phone.length - 6)}${phone.slice(-3)}`
}
